import React, { useState, useEffect, useRef } from 'react';
import { IS, SHead, Inp, Row, Sel, Txta, Btn, SavedBadge, TabBtn } from './ui';
import { resolveMarkupDefaults, canRestampMarkup, DEFAULT_PRODUCT_MARKUP_PCT, DEFAULT_FEE_MARKUP_PCT, sortInstallEvents, installEventIsPast, installEventEnd, salesTaxCodeBlendedRate, projectFieldsFromTaxCode, projectMatchesTaxCode } from '../utils';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';

export function FirmSettings(p) {
  // Prefer prop (set by ProcurementApp parent); fall back to context.
  // Tests may render this component without an AppProvider wrap and
  // pass no savePatch — that's OK; the autosave useEffect short-circuits
  // when saveFirmSettingsPatch is undefined.
  var ctx = { saveFirmSettingsPatch: null };
  try { ctx = useAppContext(); } catch (e) { /* no provider — fine for tests */ }
  var saveFirmSettingsPatch = p.savePatch || ctx.saveFirmSettingsPatch;
  var fs = useState(Object.assign({}, p.settings)); var form = fs[0]; var setForm = fs[1];
  function set(k) { return function(v) { setForm(function(prev) { var n = Object.assign({}, prev); n[k] = v; return n; }); }; }
  function save() { p.setSettings(Object.assign({}, form)); }
  // Per-field SQL UPDATE since 2026-06-06. Computes the diff between
  // local form state and parent firmSettings, sends ONLY changed
  // fields to saveFirmSettingsPatch. SQL UPDATE writes the touched
  // columns; other columns untouched. Eliminates the whole-blob
  // overwrite race that caused the "Pine Street default leaked into
  // storage" + "Toggl workspace ID dropped" bugs.
  //
  // Content-equality guard prevents the mount-time fire from sending
  // a no-op patch when form === p.settings (initial Object.assign copy).
  React.useEffect(function() {
    if (JSON.stringify(form) === JSON.stringify(p.settings)) return;
    if (!saveFirmSettingsPatch) return;
    var diff = {};
    Object.keys(form).forEach(function(k) {
      if (JSON.stringify(form[k]) !== JSON.stringify((p.settings || {})[k])) {
        diff[k] = form[k];
      }
    });
    if (Object.keys(diff).length > 0) {
      saveFirmSettingsPatch(diff);
    }
  }, [JSON.stringify(form)]);

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: -.3 }}>Firm Settings</div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Used in resale certificates, PO headers, proposals, and email footers.</div>
        </div>
        <SavedBadge on={p.saved} />
      </div>
      <SHead>Business Information</SHead>
      <Inp label="Firm Name" value={form.name || ''} onChange={set('name')} placeholder="Cimon Starr LLC" />
      <Row>
        <div style={{ flex: 2 }}><Inp label="Address" value={form.address || ''} onChange={set('address')} placeholder="340 Pine Street, Suite 302" /></div>
        <div style={{ flex: 1 }}><Inp label="City" value={form.city || ''} onChange={set('city')} placeholder="San Francisco" /></div>
      </Row>
      <Row>
        <div style={{ flex: 1 }}><Inp label="State" value={form.state || ''} onChange={set('state')} placeholder="CA" /></div>
        <div style={{ flex: 1 }}><Inp label="Zip" value={form.zip || ''} onChange={set('zip')} placeholder="94104" /></div>
        <div style={{ flex: 2 }}><Inp label="Phone" value={form.phone || ''} onChange={set('phone')} placeholder="(415) 000-0000" /></div>
      </Row>
      <Row>
        <div style={{ flex: 1 }}><Inp label="Purchasing Email" value={form.email || ''} onChange={set('email')} placeholder="purchasing@cimonstarr.com" /></div>
      </Row>
      <SHead>Tax &amp; Resale</SHead>
      <Row>
        <div style={{ flex: 1 }}><Inp label="CA Seller's Permit #" value={form.sellerPermit || ''} onChange={set('sellerPermit')} placeholder="225-379680" /></div>
      </Row>
      <div style={{ fontSize: 11, color: '#888', marginTop: -4, marginBottom: 12 }}>This number appears on all resale certificates generated for vendors.</div>
      <SHead>Principal</SHead>
      <Row>
        <div style={{ flex: 2 }}><Inp label="Principal Name" value={form.principalName || ''} onChange={set('principalName')} placeholder="Cimon Starr" /></div>
        <div style={{ flex: 2 }}><Inp label="Title" value={form.principalTitle || ''} onChange={set('principalTitle')} placeholder="Principal / Interior Designer" /></div>
      </Row>

      <SHead>Toggl Settings</SHead>
      <Inp label="Toggl Workspace ID" value={form.togglWorkspaceId || ''} onChange={set('togglWorkspaceId')} placeholder="e.g. 1234567" />
      <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Your numeric workspace ID from toggl.com → Settings → Workspace.</div>
      <Txta label="Non-Billable Toggl Projects" value={form.nonBillableTogglProjects || ''} onChange={set('nonBillableTogglProjects')} placeholder={'One project name per line — excluded from all client invoices.\nAdministration\nInternal'} rows={3} />
      <div style={{ fontSize: 11, color: '#888', marginTop: -4, marginBottom: 12 }}>Time entries in these Toggl projects will not appear on any client invoice.</div>
      <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: '12px 16px', marginTop: 16, fontSize: 12, color: '#1b5e20' }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>✓ Settings auto-save as you type</div>
        <div>These values flow into: resale certificates, PO headers, email signatures, and proposals.</div>
      </div>

      <SHead>Studio Team</SHead>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 10, lineHeight: 1.5 }}>
        Team members appear by name on project notes and internal logs instead of email addresses.<br />
        <strong>Admin</strong> grants access to Client Invoices, Finance, Reconciliation, Ledger, and other admin-gated actions throughout the app. Owner is always admin and doesn't need the box.
      </div>
      {(form.studioTeam || []).map(function(m, i) {
        return (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <div style={{ flex: 1 }}>
              <Inp label={i === 0 ? 'Name' : ''} value={m.name || ''} onChange={function(v) { setForm(function(prev) { var t = (prev.studioTeam || []).slice(); t[i] = Object.assign({}, t[i], { name: v }); return Object.assign({}, prev, { studioTeam: t }); }); }} placeholder="Cimon" />
            </div>
            <div style={{ flex: 2 }}>
              <Inp label={i === 0 ? 'Email' : ''} value={m.email || ''} onChange={function(v) { setForm(function(prev) { var t = (prev.studioTeam || []).slice(); t[i] = Object.assign({}, t[i], { email: v }); return Object.assign({}, prev, { studioTeam: t }); }); }} placeholder="c@cimonstarr.com" />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: i === 0 ? 16 : 0, fontSize: 12, color: '#555', cursor: 'pointer', whiteSpace: 'nowrap' }} title="Grants admin access (Client Invoices, Finance, Reconciliation, Ledger, Mark Sent / Client Approved on proposals).">
              <input
                type="checkbox"
                checked={!!m.hasAdminAccess}
                onChange={function(e) { var checked = e.target.checked; setForm(function(prev) { var t = (prev.studioTeam || []).slice(); t[i] = Object.assign({}, t[i], { hasAdminAccess: checked }); return Object.assign({}, prev, { studioTeam: t }); }); }}
                data-testid={'studio-team-admin-' + i}
                style={{ cursor: 'pointer' }}
              />
              Admin
            </label>
            <button onClick={function() { setForm(function(prev) { var t = (prev.studioTeam || []).filter(function(_, idx) { return idx !== i; }); return Object.assign({}, prev, { studioTeam: t }); }); }} style={{ marginTop: i === 0 ? 16 : 0, padding: '4px 10px', background: 'none', border: '1px solid #ddd', borderRadius: 5, fontSize: 11, color: '#999', cursor: 'pointer' }}>Remove</button>
          </div>
        );
      })}
      <button onClick={function() { setForm(function(prev) { return Object.assign({}, prev, { studioTeam: (prev.studioTeam || []).concat([{ name: '', email: '', hasAdminAccess: false }]) }); }); }} style={{ padding: '5px 14px', background: 'none', border: '1px solid #bbb', borderRadius: 5, fontSize: 11, color: '#555', cursor: 'pointer', marginBottom: 16 }}>+ Add Team Member</button>

    </div>
  );
}

// ── Markup re-stamp (2026-10-06) ───────────────────────────────────────
// Rates live on each item (stamped at creation). Editing a client's or a
// project's default therefore moves nothing by itself. This offers to
// re-stamp the items the client has never seen a number for — gated by
// canRestampMarkup (no proposal link, not financially committed, no PO /
// invoice pointers). Everything past that gate keeps the rate it was
// quoted at. Returns the count re-stamped.
function offerMarkupRestamp(ctx, projectsAfter, clientsAfter, projectLabels, scopeLabel) {
  var items = (ctx && ctx.items) || [];
  var updateItem = ctx && ctx.updateItem;
  if (!updateItem || projectLabels.length === 0) return 0;
  var patches = [];
  projectLabels.forEach(function(label) {
    var proj = (projectsAfter || []).find(function(pr) { return pr && pr.label === label; }) || null;
    var d = resolveMarkupDefaults(proj, clientsAfter);
    items.forEach(function(it) {
      if (!it || it.project !== label) return;
      if (!canRestampMarkup(it)) return;
      var curM = parseFloat(it.markupPct), curF = parseFloat(it.feeMarkupPct);
      var patch = {};
      if (!(isFinite(curM) && curM === d.markupPct)) patch.markupPct = d.markupPct;
      if (!(isFinite(curF) && curF === d.feeMarkupPct)) patch.feeMarkupPct = d.feeMarkupPct;
      if (Object.keys(patch).length) patches.push({ id: it.id, patch: patch });
    });
  });
  if (patches.length === 0) return 0;
  var ok = window.confirm(
    'Apply the new markup rates to ' + patches.length + ' unsent item' + (patches.length === 1 ? '' : 's') +
    ' on ' + scopeLabel + '?\n\nOnly items with no proposal, PO or invoice are touched. Everything already quoted keeps its rate.'
  );
  if (!ok) return 0;
  patches.forEach(function(x) { updateItem(x.id, x.patch); });
  return patches.length;
}

export function ClientDirectory(p) {
  var ctx = { items: [], updateItem: null, projects: [] };
  try { ctx = useAppContext() || ctx; } catch (e) { /* no provider — fine for tests */ }
  var blank = { id: null, name: '', email: '', phone: '', billingAddress: '', billingAddress2: '', billingCity: '', billingState: '', billingZip: '', financialContactName: '', financialEmail: '', billingMethod: '', hourlyRate: '', hoursGrouping: '', retainerBalance: '', notes: '', ccContacts: [], defaultMarkupPct: '', defaultFeeMarkupPct: '' };
  var es = useState(null); var editing = es[0]; var setEditing = es[1];
  var fs = useState(blank); var form = fs[0]; var setForm = fs[1];
  var ss = useState(''); var search = ss[0]; var setSearch = ss[1];
  var ccns = useState(''); var ccName = ccns[0]; var setCCName = ccns[1];
  var cces = useState(''); var ccEmail = cces[0]; var setCCEmail = cces[1];

  function set(k) { return function(v) { setForm(function(prev) { var n = Object.assign({}, prev); n[k] = v; return n; }); }; }
  function addCC() {
    if (!ccName.trim() && !ccEmail.trim()) return;
    setForm(function(prev) { var n = Object.assign({}, prev); n.ccContacts = (prev.ccContacts || []).concat([{ name: ccName.trim(), email: ccEmail.trim() }]); return n; });
    setCCName(''); setCCEmail('');
  }
  function removeCC(i) { setForm(function(prev) { var n = Object.assign({}, prev); n.ccContacts = (prev.ccContacts || []).filter(function(_, idx) { return idx !== i; }); return n; }); }
  function save() {
    if (!form.name.trim()) return;
    // Flush any typed-but-not-added CC contact into the list. Without this,
    // a user who fills in CC name/email and clicks Save without first
    // clicking "+ Add CC" loses the typed values silently.
    var pendingCC = (ccName.trim() || ccEmail.trim())
      ? [{ name: ccName.trim(), email: ccEmail.trim() }]
      : [];
    var ccList = (form.ccContacts || []).concat(pendingCC);
    var saved = Object.assign({}, form, { id: form.id || Date.now(), ccContacts: ccList });
    var nextClients = form.id ? safeClients.map(function(c) { return c.id === form.id ? saved : c; }) : [].concat(safeClients, [saved]);
    p.setClients(nextClients);
    // Rate edit on an existing client → offer to re-stamp unsent items on
    // every project linked to this client (see offerMarkupRestamp).
    var before = form.id ? safeClients.find(function(c) { return c.id === form.id; }) : null;
    if (before && (String(before.defaultMarkupPct || '') !== String(saved.defaultMarkupPct || '')
                || String(before.defaultFeeMarkupPct || '') !== String(saved.defaultFeeMarkupPct || ''))) {
      var projs = (ctx.projects || p.projects || []);
      var labels = projs.filter(function(pr) { return pr && String(pr.clientId) === String(saved.id) && !pr.archived; }).map(function(pr) { return pr.label; });
      offerMarkupRestamp(ctx, projs, nextClients, labels, saved.name || 'this client');
    }
    setEditing(null); setForm(blank); setCCName(''); setCCEmail('');
  }
  function del(id) { if (window.confirm('Remove this client?')) p.setClients(safeClients.filter(function(c) { return c.id !== id; })); }

  var safeClients = p.clients || [];
  var filtered = safeClients.filter(function(c) {
    if (!search.trim()) return true;
    var q = search.toLowerCase();
    return !q || [c.name, c.email, c.phone, c.billingAddress, c.billingCity, c.billingState, c.notes].some(function(f) { return f && String(f).toLowerCase().includes(q); });
  });

  // Subtle delete — light grey border, neutral × glyph, no red fill
  var delBtnStyle = {
    width: 24, height: 24, padding: 0,
    border: '0.5px solid #E0E0DC', borderRadius: 4,
    background: '#FFFFFF', color: '#999',
    cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };

  var tableRows = filtered.map(function(c) {
    return (
      <tr key={c.id}
        style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
        onClick={function() { setForm(Object.assign({}, c, { ccContacts: c.ccContacts || [] })); setEditing(c.id); }}
        onMouseEnter={function(e) { e.currentTarget.style.background = '#FAFAF8'; }}
        onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
        <td style={{ padding: '9px 10px', fontWeight: 600 }}>{c.name}</td>
        <td style={{ padding: '9px 10px', color: '#1a56db', fontSize: 12 }}>{c.email || '—'}</td>
        <td style={{ padding: '9px 10px', color: '#555', fontSize: 12 }}>{c.phone || '—'}</td>
        <td style={{ padding: '9px 10px', color: '#555', fontSize: 12 }}>{c.billingCity && c.billingState ? c.billingCity + ', ' + c.billingState : '—'}</td>
        <td style={{ padding: '9px 10px', color: '#888', fontSize: 11 }}>{(c.ccContacts && c.ccContacts.length > 0) ? c.ccContacts.length + ' CC contact' + (c.ccContacts.length > 1 ? 's' : '') : '—'}</td>
        <td style={{ padding: '9px 10px', width: 40 }}>
          <button
            onClick={function(e) { e.stopPropagation(); del(c.id); }}
            onMouseEnter={function(e) { e.currentTarget.style.background = '#F5F5F3'; e.currentTarget.style.color = '#666'; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.color = '#999'; }}
            style={delBtnStyle}
            title="Remove client">×</button>
        </td>
      </tr>
    );
  });

  var ccList = (form.ccContacts || []).map(function(cc, i) {
    return (
      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#f5f0ff', border: '1px solid #e1bee7', borderRadius: 6, padding: '6px 10px', marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#4527a0' }}>{cc.name || '—'}</div>
          <div style={{ fontSize: 11, color: '#7b1fa2' }}>{cc.email || '—'}</div>
        </div>
        <button onClick={function() { removeCC(i); }} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 13, flexShrink: 0 }}>✕</button>
      </div>
    );
  });

  function closeModal() { setEditing(null); setForm(blank); setCCName(''); setCCEmail(''); }

  var modal = editing ? (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={function(e) { if (e.target === e.currentTarget) closeModal(); }}>
      <div style={{ width: 720, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 18 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>{editing === 'new' ? 'New Client' : 'Edit Client'}</div>
          <div style={{ flex: 1 }} />
          <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: 22, color: '#999', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
        </div>

        <Inp label="Client Name" value={form.name} onChange={set('name')} required={true} placeholder="e.g. Smith Family" />
        <Row>
          <div style={{ flex: 2 }}><Inp label="Primary Email" value={form.email || ''} onChange={set('email')} type="email" placeholder="client@email.com" /></div>
          <div style={{ flex: 1 }}><Inp label="Phone" value={form.phone || ''} onChange={set('phone')} placeholder="(415) 000-0000" /></div>
        </Row>

        <SHead>Billing Address</SHead>
        <Row>
          <div style={{ flex: 2 }}><Inp label="Address" value={form.billingAddress || ''} onChange={set('billingAddress')} /></div>
          <div style={{ flex: 1 }}><Inp label="Address Line 2" value={form.billingAddress2 || ''} onChange={set('billingAddress2')} /></div>
        </Row>
        <Row>
          <div style={{ flex: 2 }}><Inp label="City" value={form.billingCity || ''} onChange={set('billingCity')} /></div>
          <div style={{ flex: 1 }}><Inp label="State" value={form.billingState || ''} onChange={set('billingState')} /></div>
          <div style={{ flex: 1 }}><Inp label="Zip" value={form.billingZip || ''} onChange={set('billingZip')} /></div>
        </Row>

        <SHead>CC Contacts</SHead>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Secondary people who get cc'd on proposals and invoices — accountants, business managers, financial advisors, attorneys. Optional, can be many. <strong>Note:</strong> click <em>+ Add CC</em> after typing — Save alone won't capture unsaved input.</div>
        {ccList.length > 0 && <div style={{ marginBottom: 10 }}>{ccList}</div>}
        <div style={{ background: '#FAFAF8', border: '1px dashed #d1c4e9', borderRadius: 6, padding: 10, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 6 }}>Add CC Contact</div>
          <Row>
            <div style={{ flex: 2 }}><Inp label="Name" value={ccName} onChange={setCCName} placeholder="e.g. James Chen (Financial Advisor)" /></div>
            <div style={{ flex: 2 }}><Inp label="Email" value={ccEmail} onChange={setCCEmail} type="email" placeholder="james@firm.com" /></div>
          </Row>
          <Btn variant="secondary" small={true} onClick={addCC} disabled={!ccName.trim() && !ccEmail.trim()}>+ Add CC</Btn>
        </div>

        <SHead>Financial Contact</SHead>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Primary billing recipient — receives invoices, payment requests, retainer notices. Usually the client themselves or their lead financial point of contact. Required for any client with billing.</div>
        <Row>
          <div style={{ flex: 2 }}><Inp label="Financial Contact Name" value={form.financialContactName || ''} onChange={set('financialContactName')} placeholder="e.g. Anne Kubik" /></div>
          <div style={{ flex: 2 }}><Inp label="Financial Contact Email" value={form.financialEmail || ''} onChange={set('financialEmail')} type="email" placeholder="anne@example.com" /></div>
        </Row>

        <SHead>Markup</SHead>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Defaults for new items on this client's projects. Blank = firm default ({DEFAULT_PRODUCT_MARKUP_PCT}% merchandise, {DEFAULT_FEE_MARKUP_PCT}% fees). A project can override; each item stamps its rate at creation.</div>
        <Row>
          <div style={{ flex: 1 }}><Inp label="Merchandise markup %" value={form.defaultMarkupPct == null ? '' : String(form.defaultMarkupPct)} onChange={set('defaultMarkupPct')} placeholder={String(DEFAULT_PRODUCT_MARKUP_PCT)} dataField="defaultMarkupPct" /></div>
          <div style={{ flex: 1 }}><Inp label="Fee markup % (freight, crating, install)" value={form.defaultFeeMarkupPct == null ? '' : String(form.defaultFeeMarkupPct)} onChange={set('defaultFeeMarkupPct')} placeholder={String(DEFAULT_FEE_MARKUP_PCT)} dataField="defaultFeeMarkupPct" /></div>
          <div style={{ flex: 2 }} />
        </Row>
        <SHead>Billing</SHead>
        <Row>
          <div style={{ flex: 2 }}>
            <Sel label="Billing Method" value={form.billingMethod || ''} onChange={set('billingMethod')}>
              <option value="">— Select —</option>
              <option value="retainer">Retainer</option>
              <option value="hourly">Hourly</option>
              <option value="percentage-of-procurement">Percentage of Procurement</option>
              <option value="hybrid">Hybrid</option>
            </Sel>
          </div>
          <div style={{ flex: 1 }}><Inp label="Hourly Rate ($)" value={form.hourlyRate || ''} onChange={set('hourlyRate')} placeholder="e.g. 295" /></div>
          <div style={{ flex: 2 }}>
            {/* Hours-invoice grouping (2026-08-23 — the Kubik switch).
                'person' renders the Designer Hours invoice as one row per
                team member (name · description · hours · rate · amount,
                rates visible) instead of by project phase. Rates come
                from each project's team config (Project Directory →
                Integrations) — verify them before the first send. */}
            <Sel label="Hours invoice — group by" value={form.hoursGrouping || ''} onChange={set('hoursGrouping')} data-testid="client-hours-grouping">
              <option value="">Project phase (default)</option>
              <option value="person">Team member</option>
            </Sel>
          </div>
        </Row>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 12, padding: '8px 12px', background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 6 }}>
          Retainer balance is now tracked in <strong style={{ color: '#111' }}>Client Invoices → Retainers</strong> per project. Add deposits and adjustments there.
        </div>

        <Txta label="Notes" value={form.notes || ''} onChange={set('notes')} placeholder="Any notes about this client..." rows={2} />

        <div style={{ display: 'flex', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid #eee' }}>
          <Btn onClick={save} disabled={!form.name.trim()}>Save Client</Btn>
          <Btn variant="secondary" onClick={closeModal}>Cancel</Btn>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder="Search clients..." autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off" style={{ flex: 1, maxWidth: 420, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12 }} />
        <Btn onClick={function() { setForm(blank); setEditing('new'); setCCName(''); setCCEmail(''); }}>+ Add Client</Btn>
        <SavedBadge on={p.saved} />
      </div>
      {safeClients.length === 0 && !search
        ? <div style={{ border: '1px dashed #ddd', borderRadius: 8, padding: 40, textAlign: 'center', color: '#bbb', fontSize: 13 }}>No clients yet — click + Add Client to get started</div>
        : (
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  {['Client', 'Email', 'Phone', 'Billing Location', 'CC Contacts', ''].map(function(hd) {
                    return <th key={hd} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{hd}</th>;
                  })}
                </tr>
              </thead>
              <tbody>{tableRows}</tbody>
            </table>
          </div>
        )
      }
      {modal}
    </div>
  );
}

export function ProjectDirectory(p) {
  // Context for the tax-code picker; tests may render without a provider.
  var ctx = { salesTaxCodes: [] };
  try { ctx = useAppContext() || ctx; } catch (e) { /* no provider — fine for tests */ }
  var blank = { id: null, label: '', displayName: '', clientId: null, receiverId: null, attn: '', address: '', address2: '', city: '', state: '', zip: '', rooms: [], installEvents: [], togglPrefix: '', teamMembers: [], projectCode: '', hasRetainer: false, retainerThreshold: 60000, retainerBalance: '', qbCustomerId: '', taxRate: '', taxesMerchandise: true, taxesFreight: false, taxesCrating: false, taxesInstall: false, taxesTariff: false, taxesStorage: false, taxesTime: false, taxCodeId: '', markupPct: '', feeMarkupPct: '' };
  var es = useState(null); var editing = es[0]; var setEditing = es[1];
  var fs = useState(blank); var form = fs[0]; var setForm = fs[1];
  var rns = useState(''); var rn = rns[0]; var setRn = rns[1];
  var rnums = useState(''); var rNum = rnums[0]; var setRNum = rnums[1];
  // Tabbed editor (2026-07-30 redesign — match the item page): the edit
  // view replaces the list full-width; tabs slice the SAME form object,
  // so saving is untouched by which tab is open.
  var pts = useState('general'); var projTab = pts[0]; var setProjTab = pts[1];
  // Archive / delete (2026-07-30 — the red × was a bare window.confirm
  // hard delete). Two-stage modal: archive is the recommended path;
  // permanent delete requires TYPING the project label.
  var ams = useState(null); var archiveModal = ams[0]; var setArchiveModal = ams[1];
  var sas = useState(false); var showArchived = sas[0]; var setShowArchived = sas[1];

  // Autosave-as-you-type for EXISTING projects (Cimon 2026-07-30) —
  // the billing-summary debounce pattern. New projects still need the
  // explicit Save (it mints the id). Guards: never persist a blank
  // name mid-edit; skip the first render after the form loads.
  var autosaveTimer = useRef(null);
  var autosaveArmed = useRef(null);
  var autosavePending = useRef(null); // snapshot awaiting the debounce
  var setProjectsRef = useRef(p.setProjects); setProjectsRef.current = p.setProjects;
  function flushProjectSnapshot(snapshot) {
    autosavePending.current = null;
    // Functional form — merges onto the CURRENT array, so a concurrent
    // write (QB stamp, another surface) is never clobbered by a stale
    // p.projects closure.
    setProjectsRef.current(function(prev) {
      return (prev || []).map(function(proj) { return proj.id === snapshot.id ? Object.assign({}, snapshot) : proj; });
    });
  }
  useEffect(function() {
    if (!editing || editing === 'new' || !form.id) { autosaveArmed.current = null; return; }
    if (autosaveArmed.current !== editing) { autosaveArmed.current = editing; return; } // loading the row isn't an edit
    if (!String(form.label || '').trim()) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    var snapshot = form;
    autosavePending.current = snapshot;
    autosaveTimer.current = setTimeout(function() { flushProjectSnapshot(snapshot); }, 1200);
    return function() { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [form, editing]);
  // Unmount flush — navigating to another main tab mid-debounce must not
  // drop the last keystrokes (the cleanup above cancels the timer; this
  // effect, deps [], runs its cleanup ONLY on unmount and saves whatever
  // was still pending).
  useEffect(function() {
    return function() {
      if (autosavePending.current && autosavePending.current.id) flushProjectSnapshot(autosavePending.current);
    };
  }, []);
  var tmns = useState(''); var tmn = tmns[0]; var setTmn = tmns[1];
  var tmrs = useState(''); var tmr = tmrs[0]; var setTmr = tmrs[1];
  var tmrats = useState(''); var tmrat = tmrats[0]; var setTmrat = tmrats[1];
  function set(k) { return function(v) { setForm(function(prev) { var n = Object.assign({}, prev); n[k] = v; return n; }); }; }
  function addRoom() {
    if (!rn.trim()) return;
    setForm(function(prev) {
      var n = Object.assign({}, prev);
      var updated = (prev.rooms || []).concat([{ name: rn.trim(), number: rNum.trim() }]);
      updated.sort(function(a, b) { var na = parseFloat(a.number) || 0, nb = parseFloat(b.number) || 0; if (na !== nb) return na - nb; return (a.number || '').localeCompare(b.number || ''); });
      n.rooms = updated; return n;
    });
    setRn(''); setRNum('');
  }
  function removeRoom(i) { setForm(function(prev) { var n = Object.assign({}, prev); n.rooms = (prev.rooms || []).filter(function(_, idx) { return idx !== i; }); return n; }); }
  function updateRoom(i, field, v) { setForm(function(prev) { var n = Object.assign({}, prev); n.rooms = (prev.rooms || []).map(function(r, idx) { return idx === i ? Object.assign({}, r, { [field]: v }) : r; }); return n; }); }
  function addTeamMember() {
    if (!tmn.trim()) return;
    setForm(function(prev) { var n = Object.assign({}, prev); n.teamMembers = (prev.teamMembers || []).concat([{ togglName: tmn.trim(), role: tmr.trim(), rate: tmrat.trim(), foldInto: '' }]); return n; });
    setTmn(''); setTmr(''); setTmrat('');
  }
  function removeTeamMember(i) { setForm(function(prev) { var n = Object.assign({}, prev); n.teamMembers = (prev.teamMembers || []).filter(function(_, idx) { return idx !== i; }); return n; }); }
  function updateTeamMember(i, field, v) { setForm(function(prev) { var n = Object.assign({}, prev); n.teamMembers = (prev.teamMembers || []).map(function(m, idx) { return idx === i ? Object.assign({}, m, { [field]: v }) : m; }); return n; }); }
  function save() {
    if (!form.label.trim()) return;
    var saved = Object.assign({}, form, { id: form.id || crypto.randomUUID() });
    var safeProjects = p.projects || [];
    var nextProjects = form.id ? safeProjects.map(function(proj) { return proj.id === form.id ? saved : proj; }) : safeProjects.concat([saved]);
    p.setProjects(nextProjects);
    autosavePending.current = null;
    var before = form.id ? safeProjects.find(function(proj) { return proj.id === form.id; }) : null;
    if (before && (String(before.markupPct || '') !== String(saved.markupPct || '')
                || String(before.feeMarkupPct || '') !== String(saved.feeMarkupPct || ''))) {
      offerMarkupRestamp(ctx, nextProjects, clients, [saved.label], saved.label);
    }
    setEditing(null); setForm(blank);
  }
  function archiveProject(id) {
    p.setProjects((p.projects || []).map(function(proj) {
      return proj.id === id ? Object.assign({}, proj, { archived: true, archivedAt: new Date().toISOString() }) : proj;
    }));
    setArchiveModal(null);
  }
  function restoreProject(id) {
    p.setProjects((p.projects || []).map(function(proj) {
      return proj.id === id ? Object.assign({}, proj, { archived: false }) : proj;
    }));
  }
  function deleteProjectForever(id) {
    p.setProjects((p.projects || []).filter(function(proj) { return proj.id !== id; }));
    setArchiveModal(null);
  }

  var clients = p.clients || [];
  var receivers = (p.vendors || []).filter(function(v) { return (v.vendorType || 'Vendor') === 'Receiver'; });

  var activeProjectRows = (p.projects || []).filter(function(proj) { return proj && !proj.archived; });
  var archivedProjectRows = (p.projects || []).filter(function(proj) { return proj && proj.archived; });
  var tableRows = activeProjectRows.map(function(proj) {
    var client = clients.find(function(c) { return c.id === proj.clientId; }) || null;
    var receiver = (p.vendors || []).find(function(v) { return v.id === proj.receiverId; }) || null;
    var loc = (proj.city && proj.state) ? proj.city + ', ' + proj.state : '—';
    var numRooms = (proj.rooms || []).length + ' rooms';
    return (
      <tr key={proj.id} style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }} onClick={function() { setForm(Object.assign({}, proj, { rooms: proj.rooms || [] })); setEditing(proj.id); setProjTab('general'); }}>
        <td style={{ padding: '9px 10px', fontWeight: 600 }}>{proj.label}</td>
        <td style={{ padding: '9px 10px', color: '#333', fontSize: 12 }}>{client ? client.name : '—'}</td>
        <td style={{ padding: '9px 10px', color: '#2e7d32', fontSize: 12 }}>{receiver ? receiver.name : '—'}</td>
        <td style={{ padding: '9px 10px', color: '#666', fontSize: 12 }}>{loc}</td>
        <td style={{ padding: '9px 10px', color: '#666', fontSize: 12 }}>{numRooms}</td>
        <td style={{ padding: '9px 10px' }}>
          <button onClick={function(e) { e.stopPropagation(); setArchiveModal({ project: proj, stage: 'choose', typed: '' }); }}
            title="Archive or delete this project"
            style={{ background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 5, color: '#888880', cursor: 'pointer', fontSize: 11, padding: '3px 10px' }}>
            Archive…
          </button>
        </td>
      </tr>
    );
  });

  var roomList = editing ? (form.rooms || []).map(function(r, i) {
    return (
      <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <input value={r.number || ''} onChange={function(e) { updateRoom(i, 'number', e.target.value); }} placeholder="#" style={Object.assign({}, IS, { width: 56, fontSize: 12, padding: '4px 6px' })} />
        <input value={r.name || ''} onChange={function(e) { updateRoom(i, 'name', e.target.value); }} placeholder="Room name" style={Object.assign({}, IS, { flex: 1, fontSize: 12, padding: '4px 6px' })} />
        <button onClick={function() { removeRoom(i); }} style={{ background: 'none', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}>✕</button>
      </div>
    );
  }) : null;

  var selClient = clients.find(function(c) { return c.id === form.clientId; }) || null;
  var selReceiver = (p.vendors || []).find(function(v) { return v.id === form.receiverId; }) || null;

  var clientInfoBanner = selClient ? (
    <div style={{ background: '#f5f0ff', border: '1px solid #d1c4e9', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#4527a0' }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{selClient.name}</div>
      {selClient.email && <div>✉ {selClient.email}</div>}
      {selClient.phone && <div>✆ {selClient.phone}</div>}
      {(selClient.billingCity || selClient.billingState) && <div style={{ color: '#7b1fa2', marginTop: 2 }}>Billing: {selClient.billingCity || ''}, {selClient.billingState || ''}</div>}
      {selClient.ccContacts && selClient.ccContacts.length > 0 && <div style={{ marginTop: 4, color: '#7b1fa2' }}>CC: {selClient.ccContacts.map(function(cc) { return cc.name || cc.email; }).join(', ')}</div>}
    </div>
  ) : null;

  var receiverInfoBanner = selReceiver ? (
    <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#1b5e20' }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{selReceiver.name}</div>
      {selReceiver.contact && <div>Contact: {selReceiver.contact}</div>}
      {[selReceiver.address, selReceiver.city && selReceiver.state ? selReceiver.city + ', ' + selReceiver.state + ' ' + selReceiver.zip : ''].filter(Boolean).map(function(line, i) { return <div key={i}>{line}</div>; })}
    </div>
  ) : null;

  var projTabContent = null;
  if (projTab === 'general') {
    projTabContent = (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, maxWidth: 980 }}>
        <div>
          <SHead>Project</SHead>
          <Inp label="Project Label" value={form.label} onChange={set('label')} placeholder="e.g. 26-Hoback" required={true} />
      <Inp label="Display Name (optional)" value={form.displayName || ''} onChange={set('displayName')} placeholder="e.g. Hoback Residence" />
      <Inp label="Item Number Code (max 8 chars)" value={form.projectCode || ''} onChange={function(v) { set('projectCode')(v.toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,8)); }} placeholder="e.g. 26HOB" />
      </div>
        <div>
          <SHead>Client</SHead>
      <Sel label="Client" value={form.clientId || ''} onChange={function(v) {
        var id = v ? parseInt(v) || v : null;
        // Copy-on-select (tax-code pattern): the client's markup defaults
        // land in the project fields, which stay editable.
        var cl = clients.find(function(c) { return String(c.id) === String(id); }) || null;
        setForm(function(f) { return Object.assign({}, f, {
          clientId: id,
          markupPct:    cl && cl.defaultMarkupPct    !== '' && cl.defaultMarkupPct    != null ? cl.defaultMarkupPct    : f.markupPct,
          feeMarkupPct: cl && cl.defaultFeeMarkupPct !== '' && cl.defaultFeeMarkupPct != null ? cl.defaultFeeMarkupPct : f.feeMarkupPct,
        }); });
      }}>
        <option value="">— Select client —</option>
        {clients.map(function(c) { return <option key={c.id} value={c.id}>{c.name}</option>; })}
      </Sel>
      {clientInfoBanner}
      <SHead>Markup</SHead>
      {(function() {
        var eff = resolveMarkupDefaults(form, clients);
        return <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>{'New items on this project stamp ' + eff.markupPct + '% merchandise / ' + eff.feeMarkupPct + '% fees. Blank inherits the client, then the firm default.'}</div>;
      })()}
      <Row>
        <div style={{ flex: 1 }}><Inp label="Merchandise markup %" value={form.markupPct == null ? '' : String(form.markupPct)} onChange={set('markupPct')} placeholder="inherit" dataField="markupPct" /></div>
        <div style={{ flex: 1 }}><Inp label="Fee markup %" value={form.feeMarkupPct == null ? '' : String(form.feeMarkupPct)} onChange={set('feeMarkupPct')} placeholder="inherit" dataField="feeMarkupPct" /></div>
        <div style={{ flex: 2 }} />
      </Row>
      <SHead>Receiver</SHead>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Freight forwarder or white glove delivery — used as ship-to on quotes and POs.</div>
      {receivers.length === 0
        ? <div style={{ fontSize: 11, color: '#e65100', marginBottom: 8 }}>No Receiver vendors yet — add one in the Vendors directory with type Receiver.</div>
        : (
          <Sel label="Receiver (ship-to)" value={form.receiverId || ''} onChange={function(v) { set('receiverId')(v ? parseInt(v) || v : null); }}>
            <option value="">— Select receiver —</option>
            {receivers.map(function(v) { return <option key={v.id} value={v.id}>{v.name}</option>; })}
          </Sel>
        )
      }
      {receiverInfoBanner}
      <SHead>Site Address</SHead>
      <Inp label="Attn" value={form.attn || ''} onChange={set('attn')} placeholder="Site contact name" />
      <Inp label="Address" value={form.address || ''} onChange={set('address')} />
      <Inp label="Address Line 2" value={form.address2 || ''} onChange={set('address2')} />
      <Row>
        <div style={{ flex: 2 }}><Inp label="City" value={form.city || ''} onChange={set('city')} /></div>
        <div style={{ flex: 1 }}><Inp label="State" value={form.state || ''} onChange={set('state')} /></div>
        <div style={{ flex: 1 }}><Inp label="Zip" value={form.zip || ''} onChange={set('zip')} /></div>
      </Row>
      </div>
      </div>
    );
  } else if (projTab === 'rooms') {
    projTabContent = (
      <div style={{ maxWidth: 560 }}>
        <SHead>Rooms</SHead>
      <div style={{ maxHeight: 'calc(100vh - 420px)', overflowY: 'auto', marginBottom: 10 }}>{roomList}</div>
      <Row>
        <div style={{ flex: 2 }}><Inp label="Room Name" value={rn} onChange={setRn} placeholder="e.g. Primary Suite" /></div>
        <div style={{ flex: 1 }}><Inp label="Room #" value={rNum} onChange={setRNum} placeholder="101" /></div>
      </Row>
      <Btn variant="secondary" small={true} onClick={addRoom} disabled={!rn.trim()}>+ Add Room</Btn>
      </div>
    );
  } else if (projTab === 'install') {
    projTabContent = (
      <div style={{ maxWidth: 640 }}>
        <SHead>Install Events</SHead>
      {/* Named install windows ("Furniture install", "Lighting install") —
          items pick one on their Status & Tracking tab and DERIVE their
          planned install dates from it, so moving an event here moves
          every assigned item at once. Days: 1 = single-day install;
          3 = first day + two more (prints "Aug 12 – 14" on the install
          schedule). */}
      {sortInstallEvents(form.installEvents, new Date().toISOString().slice(0, 10)).map(function(ev, i) {
        var evPast = installEventIsPast(ev, new Date().toISOString().slice(0, 10));
        function patchEv(field, value) {
          var next = (form.installEvents || []).map(function(e) {
            return e.id === ev.id ? Object.assign({}, e, (function() { var o = {}; o[field] = value; return o; })()) : e;
          });
          set('installEvents')(next);
        }
        return (
          <div key={ev.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, opacity: evPast ? 0.55 : 1 }}>
            {evPast && <span style={{ fontSize: 10, color: '#888880', letterSpacing: '0.05em', textTransform: 'uppercase', flexShrink: 0 }}>past</span>}
            <input value={ev.label || ''} placeholder="e.g. Furniture install"
              autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
              onChange={function(e) { patchEv('label', e.target.value); }}
              style={{ flex: 2, fontSize: 12, padding: '6px 8px', border: '0.5px solid #E0E0DC', borderRadius: 5 }} />
            <input type="date" value={ev.startDate || ''}
              onChange={function(e) { patchEv('startDate', e.target.value); }}
              style={{ fontSize: 12, padding: '5px 8px', border: '0.5px solid #E0E0DC', borderRadius: 5 }} />
            <input type="number" min="1" step="1" value={ev.days || ''} placeholder="1" title="Days"
              onChange={function(e) { patchEv('days', e.target.value); }}
              style={{ width: 52, fontSize: 12, padding: '5px 8px', border: '0.5px solid #E0E0DC', borderRadius: 5 }} />
            <button type="button" onClick={function() {
              // Items DERIVE their planned dates from the event — deleting
              // it would blank them retroactively. Warn with the count and
              // stamp each item's plan as manual dates so history survives.
              var refs = (p.items || []).filter(function(it) { return it && String(it.installEventId || '') === String(ev.id); });
              if (refs.length > 0) {
                var msg = refs.length + ' item' + (refs.length !== 1 ? 's' : '') + ' use' + (refs.length === 1 ? 's' : '') +
                  ' this event for planned install dates. Removing it will keep ' +
                  (ev.startDate ? 'the dates on each item as manual dates' : 'the items but they will have no planned date') + '. Remove the event?';
                if (!window.confirm(msg)) return;
                if (p.onUpdateItem && ev.startDate) {
                  refs.forEach(function(it) {
                    p.onUpdateItem(it.id, { installEventId: '', installPlannedDate: ev.startDate, installDays: String(ev.days || '') });
                  });
                }
              }
              set('installEvents')((form.installEvents || []).filter(function(e) { return e.id !== ev.id; }));
            }} style={{ border: 'none', background: 'none', color: '#888880', cursor: 'pointer', fontSize: 14, padding: '0 4px' }} title="Remove event">×</button>
          </div>
        );
      })}
      <Btn variant="secondary" small={true} onClick={function() {
        set('installEvents')((form.installEvents || []).concat([{ id: String(Date.now()), label: '', startDate: '', days: '' }]));
      }}>+ Add Install Event</Btn>
      <div style={{ fontSize: 11, color: '#888', margin: '6px 0 12px' }}>
        Items assigned to an event follow its dates — change the dates here and every assigned item's planned install updates at once.
      </div>
      </div>
    );
  } else if (projTab === 'billing') {
    projTabContent = (
      <div style={{ maxWidth: 640 }}>
        <SHead>Billing</SHead>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 12, color: '#333', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!form.hasRetainer}
          onChange={function(e) {
            var checked = e.target.checked;
            set('hasRetainer')(checked);
            // Auto-default threshold on first enable so the form has a
            // value to read. Bookkeeper can edit below. If a project
            // had hasRetainer=true previously but no threshold (pre-
            // 2026-05-11 data), the default kicks in here too.
            if (checked && !form.retainerThreshold) set('retainerThreshold')(60000);
          }} />
        <span>This project is on a retainer</span>
      </label>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>
        When checked, monthly Billing Summaries for this project will draw from the retainer balance and show retainer math. Off by default. The current balance is tracked in <strong style={{ color: '#111' }}>Client Invoices → Retainers</strong> — log deposits and adjustments there.
      </div>
      {/* Per-project retainer threshold (since 2026-05-11). Negotiated per
          contract — Anne's is $60,000; another client's might be $40,000
          or $100,000. Drives the auto-request rule on the Billing Summary
          form: when the post-period retainer balance falls below this
          number, the summary's Amount Due requests a top-up back to it.
          Hidden when the project isn't on a retainer; the field still
          round-trips on the row but doesn't affect anything. */}
      {form.hasRetainer && (
        <div style={{ marginBottom: 14 }}>
          <Inp label="Retainer threshold ($)" value={form.retainerThreshold == null ? '' : String(form.retainerThreshold)}
            onChange={function(v) {
              var n = parseFloat(String(v).replace(/[$,\s]/g, ''));
              set('retainerThreshold')(isNaN(n) ? '' : n);
            }}
            placeholder="60000" />
          <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
            When the retainer balance falls below this number after the period's work is applied, the Billing Summary auto-requests a top-up back to this amount. Bookkeeper can override per-summary.
          </div>
        </div>
      )}

      {/* Tax Configuration — Fix C Phase 3 (2026-05-27). Per-project sales-
          tax rate + the seven category flags that gate which fees are
          included in the taxable base. Read by computeItemTax (utils.js)
          at document-generation time. Rules and rates live in the world
          (state DOR / CPA / bookkeeper); Gilda records what the owner
          configures and doesn't infer jurisdictions. Every new project
          needs a fresh lookup. */}
      <SHead>Tax Configuration</SHead>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
        Pick a tax code to copy its rate and taxable categories here. Codes live at Finance → State Tax. When the rate is blank, payload builders fall back to the legacy per-item stored value.
      </div>
      {/* Tax code picker (2026-09-13). Choosing a code COPIES the blended
          rate + flags into the fields below and stamps taxCodeId; the
          fields stay editable, and the note beside the picker says
          whether they still match the code. */}
      {(function() {
        var codes = (ctx.salesTaxCodes || []).filter(function(c) { return c.active !== false || c.id === form.taxCodeId; });
        var current = codes.find(function(c) { return c.id === form.taxCodeId; }) || null;
        var matches = current ? projectMatchesTaxCode(form, current) : false;
        return (
          <Row>
            <div style={{ flex: 1 }}>
              <Sel label="Tax Code" value={form.taxCodeId || ''} dataField="taxCodeId"
                onChange={function(v) {
                  var code = codes.find(function(c) { return c.id === v; });
                  if (!code) { set('taxCodeId')(''); return; }
                  setForm(function(f) { return Object.assign({}, f, projectFieldsFromTaxCode(code)); });
                }}
              >
                <option value="">— none —</option>
                {codes.map(function(c) { return <option key={c.id} value={c.id}>{c.code + ' · ' + c.name + ' · ' + String(Math.round(salesTaxCodeBlendedRate(c) * 100000) / 1000) + '%'}</option>; })}
              </Sel>
            </div>
            <div style={{ flex: 2 }}>
              <div style={{ fontSize: 11, marginTop: 22, color: current ? (matches ? '#1A5C38' : '#D4007A') : '#888' }} data-testid="tax-code-match">
                {!current ? 'No code chosen. Rate and categories below were entered by hand.'
                  : matches ? 'Matches ' + current.code + '.'
                  : 'Differs from ' + current.code + ' — edited by hand after copying. Re-select the code to copy it again.'}
              </div>
            </div>
          </Row>
        );
      })()}
      <Row>
        <div style={{ flex: 1 }}>
          <Inp label="Tax Rate (%)"
            value={(function() {
              if (form.taxRate === '' || form.taxRate == null) return '';
              var n = parseFloat(form.taxRate);
              if (isNaN(n)) return '';
              // Stored decimal → percent display. Round to 3 decimals
              // to absorb float artifacts (0.094 × 100 = 9.39999...).
              return String(Math.round(n * 100000) / 1000);
            })()}
            onChange={function(v) {
              var trimmed = String(v).replace(/[%\s]/g, '');
              if (trimmed === '') { set('taxRate')(''); return; }
              var n = parseFloat(trimmed);
              if (isNaN(n)) { set('taxRate')(''); return; }
              // Percent entry → decimal storage. Round to 6 decimal
              // places so "9.4" round-trips to 0.094 cleanly, not
              // 0.09399999999999999.
              set('taxRate')(Math.round(n * 10000) / 1000000);
            }}
            placeholder="9.000" />
        </div>
        <div style={{ flex: 2 }}>
          <div style={{ fontSize: 11, color: '#888', marginTop: 22 }}>
            Enter as a percent: <code>9</code> for 9%, <code>9.4</code> for 9.4%. Use <code>0</code> for zero-rated export.
          </div>
        </div>
      </Row>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, margin: '10px 0' }}>
        {[
          ['taxesMerchandise', 'Merchandise'],
          ['taxesFreight', 'Freight'],
          ['taxesTariff', 'Tariff / Duty'],
          ['taxesCrating', 'Crating'],
          ['taxesInstall', 'Installation / Labor'],
          ['taxesStorage', 'Storage'],
          ['taxesTime', 'Time / Hours'],
        ].map(function(pair) {
          var key = pair[0]; var label = pair[1];
          return (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#333', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form[key]} onChange={function(e) { set(key)(e.target.checked); }} style={{ width: 14, height: 14, cursor: 'pointer' }} />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
      <div data-testid="tax-config-summary" style={{ fontSize: 11, color: '#555', background: '#fff', border: '0.5px solid #E0E0DC', borderRadius: 4, padding: '6px 10px', marginBottom: 14 }}>
        {(function() {
          var rate = parseFloat(form.taxRate);
          var rateConfigured = !(form.taxRate === '' || form.taxRate == null || isNaN(rate));
          var labels = [
            [form.taxesMerchandise !== false, 'merchandise'],
            [form.taxesFreight, 'freight'],
            [form.taxesCrating, 'crating'],
            [form.taxesInstall, 'installation'],
            [form.taxesTariff, 'tariff'],
            [form.taxesStorage, 'storage'],
            [form.taxesTime, 'time'],
          ].filter(function(pp) { return pp[0]; }).map(function(pp) { return pp[1]; });
          var catText = labels.length ? labels.join(', ') : 'none';
          if (!rateConfigured) {
            // Partial config: checkbox selections persist on save but
            // are inert until the rate is filled in. Surface the
            // selections so the user sees what's stored and understands
            // why documents still use the legacy fallback.
            return 'Rate not set. Selected categories: ' + catText + '. Payload builders fall back to stored item.fees.salesTax until a rate is entered.';
          }
          var ratePct = (rate * 100).toFixed(3).replace(/\.?0+$/, '');
          return 'Taxable categories: ' + catText + '. Effective rate: ' + ratePct + '%.';
        })()}
      </div>

      </div>
    );
  } else if (projTab === 'integrations') {
    projTabContent = (
      <div style={{ maxWidth: 760 }}>
        <SHead>Toggl Integration</SHead>
      <Inp label="Toggl Project Prefix" value={form.togglPrefix || ''} onChange={set('togglPrefix')} placeholder="e.g. HOBACK" />
      <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Time entries from Toggl projects starting with this prefix are linked to this project.</div>
      {(form.teamMembers || []).length > 0 && (
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {['Toggl Name', 'Name on invoice', 'Role', 'Rate', 'Fold Into', ''].map(function(hd) {
                  return <th key={hd} style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{hd}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {(form.teamMembers || []).map(function(m, i) {
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '5px 8px' }}><input value={m.togglName || ''} onChange={function(e) { updateTeamMember(i, 'togglName', e.target.value); }} style={Object.assign({}, IS, { width: '100%', fontSize: 11, padding: '3px 5px' })} /></td>
                    {/* Name on invoice (2026-08-23) — what prints on a
                        person-grouped hours invoice. Optional: blank =
                        the Toggl name prints. NEVER edit the Toggl Name
                        to prettify — it's the API match key; a mismatch
                        silently drops that person's hours from the pull. */}
                    <td style={{ padding: '5px 8px' }}><input value={m.invoiceName || ''} onChange={function(e) { updateTeamMember(i, 'invoiceName', e.target.value); }} placeholder="blank = Toggl name" data-testid={'team-invoice-name-' + i} style={Object.assign({}, IS, { width: '100%', fontSize: 11, padding: '3px 5px' })} /></td>
                    <td style={{ padding: '5px 8px' }}><input value={m.role || ''} onChange={function(e) { updateTeamMember(i, 'role', e.target.value); }} style={Object.assign({}, IS, { width: '100%', fontSize: 11, padding: '3px 5px' })} /></td>
                    <td style={{ padding: '5px 8px' }}><input value={m.rate || ''} onChange={function(e) { updateTeamMember(i, 'rate', e.target.value); }} style={Object.assign({}, IS, { width: 55, fontSize: 11, padding: '3px 5px' })} placeholder="$/hr" /></td>
                    <td style={{ padding: '5px 8px' }}>
                      <select value={m.foldInto || ''} onChange={function(e) { updateTeamMember(i, 'foldInto', e.target.value); }} style={Object.assign({}, IS, { fontSize: 11, padding: '3px 5px' })}>
                        <option value="">— none —</option>
                        <option value="Concept & Design Development">Concept &amp; Design Development</option>
                        <option value="Construction Management">Construction Management</option>
                        <option value="Procurement">Procurement</option>
                        <option value="Project Administration">Project Administration</option>
                      </select>
                    </td>
                    <td style={{ padding: '5px 8px' }}>
                      <button onClick={function() { removeTeamMember(i); }} style={{ background: 'none', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ background: '#fafafa', border: '1px dashed #ddd', borderRadius: 6, padding: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 6 }}>Add Team Member</div>
        <Row>
          <div style={{ flex: 2 }}><Inp label="Toggl Name" value={tmn} onChange={setTmn} placeholder="Name as shown in Toggl" /></div>
          <div style={{ flex: 2 }}><Inp label="Role" value={tmr} onChange={setTmr} placeholder="e.g. Designer" /></div>
          <div style={{ flex: 1 }}><Inp label="Rate ($/hr)" value={tmrat} onChange={setTmrat} placeholder="175" /></div>
        </Row>
        <Btn variant="secondary" small={true} onClick={addTeamMember} disabled={!tmn.trim()}>+ Add Team Member</Btn>
      </div>
      </div>
    );
  }

  var editPanel = editing ? (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
        <Btn variant="secondary" small={true} onClick={function() { if (autosavePending.current && autosavePending.current.id) flushProjectSnapshot(autosavePending.current); setEditing(null); setForm(blank); }}>← All Projects</Btn>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#111111' }}>
          {editing === 'new' ? 'New Project' : (form.label || 'Edit Project')}
          {form.displayName ? <span style={{ fontWeight: 400, color: '#888880', marginLeft: 10, fontSize: 13 }}>{form.displayName}</span> : null}
        </div>
        <SavedBadge on={p.saved} />
        <div style={{ flex: 1 }} />
        <Btn onClick={save} disabled={!form.label.trim()}>{editing === 'new' ? 'Create Project' : 'Save & Close'}</Btn>
      </div>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E0E0DC', marginBottom: 20 }}>
        <TabBtn label="General" active={projTab === 'general'} onClick={function() { setProjTab('general'); }} />
        <TabBtn label="Rooms" active={projTab === 'rooms'} onClick={function() { setProjTab('rooms'); }} />
        <TabBtn label="Install" active={projTab === 'install'} onClick={function() { setProjTab('install'); }} />
        <TabBtn label="Billing &amp; Tax" active={projTab === 'billing'} onClick={function() { setProjTab('billing'); }} />
        <TabBtn label="Integrations" active={projTab === 'integrations'} onClick={function() { setProjTab('integrations'); }} />
      </div>
      {projTabContent}
      {editing === 'new' && (
        <div style={{ fontSize: 11, color: '#888', marginTop: 18 }}>
          New projects save when you click Create Project. Existing projects save as you type.
        </div>
      )}
    </div>
  ) : null;

  if (editing) return editPanel;
  return (
    <div>
      <div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
          <Btn onClick={function() { setForm(blank); setEditing('new'); setProjTab('general'); }}>+ Add Project</Btn>
          <SavedBadge on={p.saved} />
        </div>
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {['Project', 'Client', 'Receiver', 'Site', 'Rooms', ''].map(function(hd) {
                  return <th key={hd} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{hd}</th>;
                })}
              </tr>
            </thead>
            <tbody>{tableRows}</tbody>
          </table>
        </div>
        {archivedProjectRows.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <button onClick={function() { setShowArchived(!showArchived); }}
              style={{ background: 'none', border: 'none', color: '#888880', cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline', textUnderlineOffset: 2 }}>
              {showArchived ? 'Hide archived' : 'Show archived (' + archivedProjectRows.length + ')'}
            </button>
            {showArchived && (
              <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', marginTop: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <tbody>
                    {archivedProjectRows.map(function(proj) {
                      return (
                        <tr key={proj.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '9px 10px', color: '#888880' }}>{proj.label}</td>
                          <td style={{ padding: '9px 10px', color: '#bbb', fontSize: 11 }}>
                            archived {proj.archivedAt ? String(proj.archivedAt).slice(0, 10) : ''}
                          </td>
                          <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                            <button onClick={function() { restoreProject(proj.id); }}
                              style={{ background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 5, color: '#111111', cursor: 'pointer', fontSize: 11, padding: '3px 10px', marginRight: 8 }}>
                              Restore
                            </button>
                            <button onClick={function() { setArchiveModal({ project: proj, stage: 'delete', typed: '' }); }}
                              style={{ background: 'none', border: 'none', color: '#B3261E', cursor: 'pointer', fontSize: 11, padding: '3px 4px' }}>
                              Delete permanently…
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      {archiveModal && (function() {
        var proj = archiveModal.project;
        var refItems = (p.items || []).filter(function(it) { return it && it.project === proj.label; });
        var typedOk = archiveModal.typed === proj.label;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={function() { setArchiveModal(null); }}>
            <div onClick={function(e) { e.stopPropagation(); }}
              style={{ background: '#fff', borderRadius: 8, padding: 24, width: 460, boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
              {archiveModal.stage === 'choose' ? (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#111111', marginBottom: 10 }}>Archive {proj.label}?</div>
                  <div style={{ fontSize: 12.5, color: '#555552', lineHeight: 1.55, marginBottom: 8 }}>
                    Archiving hides the project from the active list and from new-work pickers (new items, schedules, billing).
                    Everything already recorded — {refItems.length} item{refItems.length !== 1 ? 's' : ''}, invoices, billing history,
                    schedules — keeps working, because the project record stays. You can restore it any time.
                  </div>
                  <div style={{ fontSize: 11.5, color: '#888880', lineHeight: 1.5, marginBottom: 16 }}>
                    Deleting is different: the record is removed and tax config, receiver, rooms and site info stop resolving
                    for everything that references {proj.label}. Almost always wrong for a project with history.
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Btn onClick={function() { archiveProject(proj.id); }}>Archive project</Btn>
                    <Btn variant="secondary" onClick={function() { setArchiveModal(null); }}>Cancel</Btn>
                    <div style={{ flex: 1 }} />
                    <button onClick={function() { setArchiveModal({ project: proj, stage: 'delete', typed: '' }); }}
                      style={{ background: 'none', border: 'none', color: '#B3261E', cursor: 'pointer', fontSize: 11.5, padding: 0, textDecoration: 'underline', textUnderlineOffset: 2 }}>
                      Delete permanently…
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#B3261E', marginBottom: 10 }}>Permanently delete {proj.label}?</div>
                  <div style={{ fontSize: 12.5, color: '#555552', lineHeight: 1.55, marginBottom: 8 }}>
                    This cannot be undone. {refItems.length} item{refItems.length !== 1 ? 's' : ''} plus any proposals, invoices,
                    billing history and schedules referencing {proj.label} are NOT deleted — but the project&apos;s tax
                    configuration, receiver, rooms, install events and site address are gone, and those records stop resolving them.
                  </div>
                  <div style={{ fontSize: 12, color: '#111111', marginBottom: 6 }}>
                    Type the project label <strong>{proj.label}</strong> to confirm:
                  </div>
                  <input value={archiveModal.typed} autoFocus
                    autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
                    onChange={function(e) { setArchiveModal(Object.assign({}, archiveModal, { typed: e.target.value })); }}
                    placeholder={proj.label}
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '7px 10px', border: '1px solid ' + (typedOk ? '#1A5C38' : '#E0E0DC'), borderRadius: 5, marginBottom: 14 }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn variant="danger" disabled={!typedOk} onClick={function() { if (typedOk) deleteProjectForever(proj.id); }}>Delete forever</Btn>
                    <Btn variant="secondary" onClick={function() { setArchiveModal(null); }}>Cancel</Btn>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
